import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import ImportResult from './import/ImportResult';
import ImportReview from './import/ImportReview';
import ImportUploader from './import/ImportUploader';
import ImportProgress from './import/ImportProgress';
import StepIndicator from './import/StepIndicator';
import ImportFooter from './import/ImportFooter';
import { useBatchImport } from './import/useBatchImport';
import { fieldsForType } from './import/importFields';

/**
 * 3-step batch import dialog (Upload → Review → Complete).
 * Pure orchestrator — state machine + API calls live in `useBatchImport`,
 * step views in `ImportUploader`/`ImportReview`/`ImportResult`,
 * progress in `StepIndicator`, and buttons in `ImportFooter`.
 */
const BatchImportModal = ({ isOpen, onClose, importType, onImportComplete }) => {
  const {
    step,
    setStep,
    file,
    setFile,
    parsing,
    parseProgress,
    parsedData,
    duplicateCheck,
    importing,
    importResult,
    showAllFields,
    setShowAllFields,
    handleClose,
    handleParse,
    handleImport,
  } = useBatchImport({ importType, isOpen, onImportComplete, onClose });

  const fields = fieldsForType(importType);
  const recordCount = duplicateCheck?.records_to_import?.length || 0;

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: 'Manrope' }}>
            Import {importType === 'patient' ? 'Patients' : 'Employees'}
          </DialogTitle>
          <DialogDescription>
            Upload a CSV, Excel, PDF, or Word file containing {importType} data
          </DialogDescription>
        </DialogHeader>

        <StepIndicator step={step} />

        {step === 1 && (
          <>
            {parsing && parseProgress && <ImportProgress progress={parseProgress} />}
            <ImportUploader
              importType={importType}
              fields={fields}
              file={file}
              setFile={setFile}
              showAllFields={showAllFields}
              setShowAllFields={setShowAllFields}
            />
          </>
        )}

        {step === 2 && parsedData && (
          <ImportReview
            parsedData={parsedData}
            duplicateCheck={duplicateCheck}
            importType={importType}
          />
        )}

        {step === 3 && importResult && (
          <ImportResult result={importResult} importType={importType} />
        )}

        <ImportFooter
          step={step}
          onCancel={handleClose}
          onParse={handleParse}
          onBack={() => setStep(1)}
          onImport={handleImport}
          onClose={handleClose}
          canParse={!!file}
          parsing={parsing}
          recordCount={recordCount}
          importing={importing}
        />
      </DialogContent>
    </Dialog>
  );
};

export default BatchImportModal;
